package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.text.InlineTextContent
import androidx.compose.foundation.text.appendInlineContent
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.Placeholder
import androidx.compose.ui.text.PlaceholderVerticalAlign
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.em
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.green_7
import one.healthium.uscreen.domain.models.LabelBlock
import one.healthium.uscreen.domain.models.TextAlignment
import one.healthium.uscreen.domain.models.UScreenTypography
import one.healthium.uscreen.ui.components.AnimatableCalendarWheel
import one.healthium.uscreen.ui.components.dpToEm
import one.healthium.uscreen.ui.text.toAnnotatedString
import one.healthium.base.utils.localizedName
import one.healthium.uscreen.ui.util.resolveNamedColor
import one.healthium.uscreen.ui.util.resolvePlaceholders
import one.healthium.uscreen.ui.util.uscreenPadding
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days

private const val ANIMATABLE_MARKER = "[animatable_in_3months_short]"
private const val INLINE_CONTENT_ID = "animatable_in_3months_short"

@Composable
fun UScreenLabelBlock(
    modifier: Modifier = Modifier,
    block: LabelBlock,
    placeholders: Map<String, String> = emptyMap(),
    fillWidth: Boolean = true,
    defaultHorizontalPadding: Int = 24,
) {
    val textColor = block.color?.let { resolveNamedColor(it) } ?: Color.Unspecified
    val resolvedText = block.text.resolvePlaceholders(placeholders)
    val rawString = resolvedText.toAnnotatedString().text
    val hasAnimatable = ANIMATABLE_MARKER in rawString
    val style = block.typography.toTextStyle()
    val widthModifier = if (fillWidth) Modifier.fillMaxWidth() else Modifier

    if (hasAnimatable) {
        AnimatableLabelContent(
            resolvedText = resolvedText,
            style = style,
            textColor = textColor,
            textAlign = block.align.toComposeTextAlign(),
            modifier = modifier
                .then(widthModifier)
                .uscreenPadding(block.padding, defaultHorizontal = defaultHorizontalPadding),
        )
    } else {
        Text(
            text = resolvedText.toAnnotatedString(),
            style = style,
            textAlign = block.align.toComposeTextAlign(),
            color = textColor,
            modifier = modifier
                .then(widthModifier)
                .uscreenPadding(block.padding, defaultHorizontal = defaultHorizontalPadding),
        )
    }
}

@Composable
private fun AnimatableLabelContent(
    resolvedText: one.healthium.uscreen.domain.models.StyledTextModel,
    style: TextStyle,
    textColor: Color,
    textAlign: TextAlign,
    modifier: Modifier,
) {
    val in3monthsDays = 90
    val in3monthsShortDays = 75
    val now = Clock.System.now()
    val startDate = now.plus(in3monthsDays.days)
    val endDate = now.plus(in3monthsShortDays.days)

    val datesList = remember(startDate, endDate) {
        val left = if (startDate < endDate) startDate else endDate
        val right = if (startDate < endDate) endDate else startDate
        val reverse = startDate > endDate
        val dates = mutableListOf<String>()
        var current = left
        while (current <= right) {
            val dt = current.toLocalDateTime(TimeZone.currentSystemDefault())
            val formatted = "${dt.month.localizedName(abbreviated = true)} ${dt.day}"
            if (reverse) dates.add(0, formatted) else dates.add(formatted)
            current = current.plus(1.days)
        }
        dates
    }

    val textMeasurer = rememberTextMeasurer()
    val density = LocalDensity.current
    val wheelStyle = style.copy(color = green_7)
    val (wheelWidth, wheelHeight) = remember(datesList, wheelStyle) {
        var maxW = 0
        var maxH = 0
        for (date in datesList) {
            val result = textMeasurer.measure(text = date, style = wheelStyle)
            if (result.size.width > maxW) maxW = result.size.width
            if (result.size.height > maxH) maxH = result.size.height
        }
        with(density) { maxW.toDp() to maxH.toDp() }
    }

    val annotated = resolvedText.toAnnotatedString()
    val markerIndex = annotated.text.indexOf(ANIMATABLE_MARKER)
    val finalAnnotated = if (markerIndex >= 0) {
        buildAnnotatedString {
            append(annotated, 0, markerIndex)
            appendInlineContent(INLINE_CONTENT_ID)
            append(annotated, markerIndex + ANIMATABLE_MARKER.length, annotated.length)
        }
    } else {
        annotated
    }

    val inlineContent = mapOf(
        INLINE_CONTENT_ID to InlineTextContent(
            placeholder = Placeholder(
                width = dpToEm(wheelWidth, style.fontSize),
                height = 1.em,
                placeholderVerticalAlign = PlaceholderVerticalAlign.TextCenter,
            ),
            children = {
                AnimatableCalendarWheel(
                    modifier = Modifier.fillMaxSize(),
                    datesList = datesList,
                    height = wheelHeight,
                    style = wheelStyle,
                )
            },
        ),
    )

    Text(
        text = finalAnnotated,
        style = style,
        textAlign = textAlign,
        color = textColor,
        inlineContent = inlineContent,
        modifier = modifier,
    )
}

@Composable
fun UScreenTypography.toTextStyle(): TextStyle = when (this) {
    UScreenTypography.Title -> AppTheme.textStyles.uscreenTitle
    UScreenTypography.Subtitle -> AppTheme.textStyles.uscreenSubtitle
    UScreenTypography.Body -> AppTheme.textStyles.uscreenBody
    UScreenTypography.Caption -> AppTheme.textStyles.uscreenCaption
    UScreenTypography.Emoji -> AppTheme.textStyles.uscreenEmoji
}

fun TextAlignment.toComposeTextAlign(): TextAlign = when (this) {
    TextAlignment.Left -> TextAlign.Start
    TextAlignment.Center -> TextAlign.Center
    TextAlignment.Right -> TextAlign.End
}
